// <synth-slider> — vertical fader for envelopes / levels.
//
// Same config + interaction contract as <synth-knob> (pointer/wheel/keyboard,
// dblclick reset, emits `input` CustomEvent<number>), drawn as a track + fill +
// handle. Light DOM for global styling.

import { normToValue, valueToNorm, formatValue, type Taper } from '../taper';

export interface SliderConfig {
  label: string;
  min: number;
  max: number;
  value: number;
  default: number;
  taper?: Taper;
  step?: number;
  unit?: string;
}

export class Slider extends HTMLElement {
  private cfg: SliderConfig = { label: '', min: 0, max: 1, value: 0, default: 0 };
  private norm = 0;
  private dragging = false;

  private fill!: HTMLDivElement;
  private handle!: HTMLDivElement;
  private track!: HTMLDivElement;
  private readout!: HTMLDivElement;

  connectedCallback(): void {
    if (this.dataset.built) return;
    this.dataset.built = '1';
    this.build();
    this.render();
  }

  configure(cfg: SliderConfig): void {
    this.cfg = { taper: 'linear', step: 0, unit: '', ...cfg };
    this.norm = valueToNorm(cfg.value, cfg.min, cfg.max, this.cfg.taper);
    if (this.fill) this.render();
  }

  setValue(value: number): void {
    this.cfg.value = value;
    this.norm = valueToNorm(value, this.cfg.min, this.cfg.max, this.cfg.taper);
    if (this.fill) this.render();
  }

  get value(): number {
    return this.cfg.value;
  }

  private build(): void {
    this.classList.add('slider');
    this.tabIndex = 0;
    this.setAttribute('role', 'slider');
    this.innerHTML = `
      <div class="slider-track">
        <div class="slider-fill"></div>
        <div class="slider-handle"></div>
      </div>
      <div class="slider-label"></div>
      <div class="slider-readout"></div>`;
    this.track = this.querySelector('.slider-track') as HTMLDivElement;
    this.fill = this.querySelector('.slider-fill') as HTMLDivElement;
    this.handle = this.querySelector('.slider-handle') as HTMLDivElement;
    this.readout = this.querySelector('.slider-readout') as HTMLDivElement;
    (this.querySelector('.slider-label') as HTMLDivElement).textContent = this.cfg.label;

    this.addEventListener('pointerdown', this.onPointerDown);
    this.addEventListener('wheel', this.onWheel, { passive: false });
    this.addEventListener('keydown', this.onKeyDown);
    this.addEventListener('dblclick', this.onReset);
  }

  private render(): void {
    const pct = (this.norm * 100).toFixed(1);
    this.fill.style.height = `${pct}%`;
    this.handle.style.bottom = `${pct}%`;
    this.readout.textContent = formatValue(this.cfg.value, this.cfg.unit ?? '');
    this.setAttribute('aria-valuemin', String(this.cfg.min));
    this.setAttribute('aria-valuemax', String(this.cfg.max));
    this.setAttribute('aria-valuenow', String(this.cfg.value));
    this.setAttribute('aria-label', this.cfg.label);
  }

  private setNorm(n: number, emit = true): void {
    this.norm = Math.min(1, Math.max(0, n));
    const value = normToValue(
      this.norm,
      this.cfg.min,
      this.cfg.max,
      this.cfg.taper,
      this.cfg.step,
    );
    this.cfg.value = value;
    this.render();
    if (emit) this.dispatchEvent(new CustomEvent<number>('input', { detail: value }));
  }

  private normFromEvent(clientY: number): number {
    const rect = this.track.getBoundingClientRect();
    return (rect.bottom - clientY) / rect.height;
  }

  private onPointerDown = (e: PointerEvent): void => {
    this.dragging = true;
    this.setPointerCapture(e.pointerId);
    this.setNorm(this.normFromEvent(e.clientY));
    this.addEventListener('pointermove', this.onPointerMove);
    this.addEventListener('pointerup', this.onPointerUp);
    e.preventDefault();
  };

  private onPointerMove = (e: PointerEvent): void => {
    if (this.dragging) this.setNorm(this.normFromEvent(e.clientY));
  };

  private onPointerUp = (e: PointerEvent): void => {
    this.dragging = false;
    this.releasePointerCapture(e.pointerId);
    this.removeEventListener('pointermove', this.onPointerMove);
    this.removeEventListener('pointerup', this.onPointerUp);
  };

  private onWheel = (e: WheelEvent): void => {
    e.preventDefault();
    const fine = e.shiftKey ? 0.005 : 0.02;
    this.setNorm(this.norm - Math.sign(e.deltaY) * fine);
  };

  private onKeyDown = (e: KeyboardEvent): void => {
    const fine = e.shiftKey ? 0.005 : 0.02;
    switch (e.key) {
      case 'ArrowUp':
        this.setNorm(this.norm + fine);
        break;
      case 'ArrowDown':
        this.setNorm(this.norm - fine);
        break;
      case 'Home':
        this.setNorm(0);
        break;
      case 'End':
        this.setNorm(1);
        break;
      default:
        return;
    }
    e.preventDefault();
  };

  private onReset = (): void => {
    this.setValue(this.cfg.default);
    this.dispatchEvent(new CustomEvent<number>('input', { detail: this.cfg.default }));
  };
}

customElements.define('synth-slider', Slider);
